import { useEffect, useState } from 'react';
import {
  createPayPreset,
  deletePayPreset,
  getPayPresets,
  updatePayPreset,
  type PayPreset,
  type PresetDraftItem,
} from './admin-api';
import { ConfirmModal } from './dialogs';
import { PctBar } from './PctBar';

const emptyRow = (): PresetDraftItem => ({ label: '', pct: 0, due_offset: 0 });

// Редактор одного пресета (создание или правка существующего).
function PresetForm({
  initial,
  onSubmit,
  onDelete,
  submitLabel,
}: {
  initial: { name: string; items: PresetDraftItem[] };
  onSubmit: (draft: { name: string; items: PresetDraftItem[] }) => Promise<void>;
  onDelete?: () => Promise<void>;
  submitLabel: string;
}) {
  const [name, setName] = useState(initial.name);
  const [items, setItems] = useState<PresetDraftItem[]>(initial.items);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);

  const sum = items.reduce((s, it) => s + (Number(it.pct) || 0), 0);
  const setItem = (i: number, patch: Partial<PresetDraftItem>) =>
    setItems((arr) => arr.map((it, j) => (j === i ? { ...it, ...patch } : it)));

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      await onSubmit({ name: name.trim(), items });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Ошибка сохранения.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="glass pad" style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <input
          className="grow"
          value={name}
          disabled={busy}
          placeholder="Название пресета (напр. 40% аванс / 60% по факту)"
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      {items.map((it, i) => (
        <div className="row" key={i} style={{ cursor: 'default', gap: 6 }}>
          <input
            className="grow"
            value={it.label}
            disabled={busy}
            placeholder="Название транша (напр. Аванс)"
            onChange={(e) => setItem(i, { label: e.target.value })}
          />
          <input
            type="number"
            style={{ width: 68 }}
            value={it.pct}
            disabled={busy}
            title="доля, %"
            onChange={(e) => setItem(i, { pct: Number(e.target.value) })}
          />
          <span className="muted small">%</span>
          <input
            type="number"
            style={{ width: 76 }}
            value={it.due_offset}
            disabled={busy}
            title="срок: дней от подписания (может быть отрицательным)"
            onChange={(e) => setItem(i, { due_offset: Number(e.target.value) })}
          />
          <span className="muted small">дн</span>
          <button
            className="icon-btn"
            disabled={busy || items.length <= 1}
            title="Удалить транш"
            onClick={() => setItems((arr) => arr.filter((_, j) => j !== i))}
          >
            🗑
          </button>
        </div>
      ))}

      {sum > 0 && (
        <div style={{ margin: '4px 0 10px' }}>
          <PctBar items={items.map((it) => ({ pct: it.pct, label: it.label }))} />
        </div>
      )}

      <div className="row" style={{ cursor: 'default' }}>
        <button
          className="btn ghost sm"
          disabled={busy}
          onClick={() => setItems((arr) => [...arr, emptyRow()])}
        >
          + Транш
        </button>
        <span className="grow" />
        <span className="rmeta">
          Сумма долей: <b style={{ color: sum === 100 ? 'var(--grn)' : 'var(--red)' }}>{sum}%</b>
          {sum !== 100 && ' (норма 100%)'}
        </span>
      </div>

      {msg && <p className="err">{msg}</p>}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button className="btn sm" disabled={busy || !name.trim() || sum !== 100} onClick={save}>
          {submitLabel}
        </button>
        {onDelete && (
          <button className="btn ghost sm" disabled={busy} onClick={() => setConfirmDel(true)}>
            Удалить
          </button>
        )}
      </div>

      {confirmDel && onDelete && (
        <ConfirmModal
          title="Удаление пресета"
          message={`Удалить пресет «${name}»? Действие необратимо.`}
          confirmLabel="Удалить"
          onConfirm={onDelete}
          onClose={() => setConfirmDel(false)}
        />
      )}
    </div>
  );
}

export function PresetsPanel() {
  const [presets, setPresets] = useState<PayPreset[] | null>(null);
  const [adding, setAdding] = useState(false);

  const load = () => getPayPresets().then((r) => setPresets(r.presets));
  useEffect(() => {
    load();
  }, []);

  if (!presets) return <p className="muted">Загрузка…</p>;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <p className="muted small grow" style={{ margin: 0 }}>
          Пресеты применяются одной кнопкой в карточке сделки (вкладка «Платежи»). Сумма долей —
          100%.
        </p>
        <button className="btn sm" onClick={() => setAdding((v) => !v)}>
          {adding ? 'Отмена' : '+ Пресет'}
        </button>
      </div>

      {adding && (
        <PresetForm
          initial={{
            name: '',
            items: [
              { label: 'Аванс', pct: 50, due_offset: 0 },
              { label: 'Остаток', pct: 50, due_offset: 30 },
            ],
          }}
          submitLabel="Создать пресет"
          onSubmit={async (d) => {
            await createPayPreset(d.name, d.items);
            setAdding(false);
            await load();
          }}
        />
      )}

      {presets.map((p) => (
        <PresetForm
          key={p.id}
          initial={{
            name: p.name,
            items: p.items.map((i) => ({ label: i.label, pct: i.pct, due_offset: i.due_offset })),
          }}
          submitLabel="Сохранить"
          onSubmit={async (d) => {
            await updatePayPreset(p.id, d);
            await load();
          }}
          onDelete={async () => {
            await deletePayPreset(p.id);
            await load();
          }}
        />
      ))}
      {presets.length === 0 && !adding && <p className="muted">Пресетов пока нет.</p>}
    </>
  );
}
